import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Building2, BookUser, Pencil, RotateCcw, X, Check } from 'lucide-react';
import { paymentReconApi } from '../../services/paymentReconApi';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../ui/ConfirmModal';
import { formatDate } from '../../utils/paymentReconFormat';
import type { ReconBankAccount, ReconAlias } from '../../types/paymentRecon';

interface Props {
    canEdit: boolean;
    companyCode: string;
    bankAccounts: ReconBankAccount[];
    onBankAccountsChanged: () => void;
}

const EMPTY_FORM = { id: null as number | null, name: '', key: '', iban: '', currency: 'EUR' };

export function SettingsTab({ canEdit, companyCode, bankAccounts, onBankAccountsChanged }: Props) {
    const { toast } = useToast();
    const [aliases, setAliases] = useState<ReconAlias[]>([]);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [aliasToDelete, setAliasToDelete] = useState<ReconAlias | null>(null);
    const [bankToDelete, setBankToDelete] = useState<ReconBankAccount | null>(null);

    const loadAliases = useCallback(async () => {
        try {
            const res = await paymentReconApi.listAliases(companyCode);
            setAliases(res.aliases);
        } catch { /* ignore */ }
    }, [companyCode]);
    useEffect(() => { void loadAliases(); }, [loadAliases]);

    const resetForm = () => setForm({ ...EMPTY_FORM });

    const startEdit = (b: ReconBankAccount) => {
        setForm({ id: b.Id, name: b.Name, key: b.BankAccountKey, iban: b.Iban || '', currency: b.Currency });
    };

    const saveBank = async () => {
        if (!form.name.trim() || !form.key.trim()) { toast('Name and key are required', 'info'); return; }
        try {
            await paymentReconApi.saveBankAccount({
                id: form.id ?? undefined,
                companyCode,
                name: form.name.trim(), bankAccountKey: form.key.trim(),
                iban: form.iban.trim() || null, currency: form.currency.trim() || 'EUR',
                isActive: true,
            });
            toast(form.id ? 'Bank account updated' : 'Bank account added', 'success');
            resetForm();
            onBankAccountsChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        }
    };

    const reactivate = async (b: ReconBankAccount) => {
        try {
            await paymentReconApi.saveBankAccount({
                id: b.Id, companyCode, name: b.Name, bankAccountKey: b.BankAccountKey,
                iban: b.Iban, currency: b.Currency, isActive: true,
            });
            toast('Bank account reactivated', 'success');
            onBankAccountsChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        }
    };

    const confirmDeleteBank = async () => {
        if (!bankToDelete) return;
        const b = bankToDelete;
        setBankToDelete(null);
        try {
            await paymentReconApi.deleteBankAccount(b.Id);
            toast('Bank account deactivated', 'success');
            if (form.id === b.Id) resetForm();
            onBankAccountsChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        }
    };

    const confirmDeleteAlias = async () => {
        if (!aliasToDelete) return;
        const id = aliasToDelete.Id;
        setAliasToDelete(null);
        try {
            await paymentReconApi.deleteAlias(id);
            toast('Alias removed', 'success');
            void loadAliases();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        }
    };

    return (
        <div className="grid gap-6 md:grid-cols-2">
            {/* Bank accounts */}
            <div className="rounded-lg border border-slate-200 p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Building2 className="h-4 w-4" /> Bank accounts</h3>
                <div className="space-y-2">
                    {bankAccounts.length === 0 && <div className="text-sm text-slate-400">None yet.</div>}
                    {bankAccounts.map(b => (
                        <div key={b.Id} className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${b.IsActive ? 'border-slate-100' : 'border-slate-100 bg-slate-50 opacity-70'}`}>
                            <div className="min-w-0">
                                <div className="font-medium text-slate-700">{b.Name}</div>
                                <div className="truncate text-xs text-slate-400">{b.Iban || b.BankAccountKey} · {b.Currency}</div>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className={`rounded-full px-2 py-0.5 text-xs ${b.IsActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{b.IsActive ? 'active' : 'inactive'}</span>
                                {canEdit && b.IsActive && (
                                    <>
                                        <button onClick={() => startEdit(b)} title="Edit" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => setBankToDelete(b)} title="Deactivate" className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </>
                                )}
                                {canEdit && !b.IsActive && (
                                    <button onClick={() => reactivate(b)} title="Reactivate" className="rounded p-1 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600">
                                        <RotateCcw className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {canEdit && (
                    <div className="mt-4 space-y-2 border-t border-slate-100 pt-3">
                        {form.id !== null && (
                            <div className="flex items-center justify-between text-xs font-medium text-amber-700">
                                <span>Editing “{form.name || 'account'}”</span>
                                <button onClick={resetForm} className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /> cancel</button>
                            </div>
                        )}
                        <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Account name (e.g. Crédit Agricole EUR)" className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                        <div className="flex gap-2">
                            <input value={form.key} onChange={(e) => setForm(f => ({ ...f, key: e.target.value }))} placeholder="Short key" className="w-1/3 rounded border border-slate-300 px-2 py-1.5 text-sm" />
                            <input value={form.iban} onChange={(e) => setForm(f => ({ ...f, iban: e.target.value }))} placeholder="IBAN (optional)" className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm" />
                            <input value={form.currency} onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))} placeholder="EUR" className="w-16 rounded border border-slate-300 px-2 py-1.5 text-sm" />
                        </div>
                        <button onClick={saveBank} className="inline-flex items-center gap-1 rounded-md bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">
                            {form.id !== null ? <><Check className="h-4 w-4" /> Update account</> : <><Plus className="h-4 w-4" /> Add account</>}
                        </button>
                    </div>
                )}
            </div>

            {/* Learned aliases */}
            <div className="rounded-lg border border-slate-200 p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><BookUser className="h-4 w-4" /> Learned payers ({aliases.length})</h3>
                <div className="max-h-96 space-y-1 overflow-y-auto">
                    {aliases.length === 0 && <div className="text-sm text-slate-400">No learned mappings yet. They build up as you confirm matches.</div>}
                    {aliases.map(a => (
                        <div key={a.Id} className="flex items-center justify-between rounded border border-slate-100 px-3 py-1.5 text-sm">
                            <div className="min-w-0">
                                <div className="truncate text-slate-700">{a.CounterpartyNormalized}</div>
                                <div className="truncate text-xs text-slate-400">→ {a.CustomerName || a.CustomerCode} · used {a.TimesUsed}× · {formatDate(a.LastUsedAt)}</div>
                            </div>
                            {canEdit && (
                                <button onClick={() => setAliasToDelete(a)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {bankToDelete && (
                <ConfirmModal
                    title="Deactivate bank account"
                    message={`Deactivate “${bankToDelete.Name}”? It will be hidden from new imports but stays linked to historical imports. You can reactivate it later.`}
                    confirmLabel="Deactivate"
                    variant="danger"
                    onConfirm={confirmDeleteBank}
                    onCancel={() => setBankToDelete(null)}
                />
            )}
            {aliasToDelete && (
                <ConfirmModal
                    title="Forget learned payer"
                    message={`Forget the mapping “${aliasToDelete.CounterpartyNormalized}” → ${aliasToDelete.CustomerName || aliasToDelete.CustomerCode}? Future payments from this payer won’t auto-resolve.`}
                    confirmLabel="Forget"
                    variant="danger"
                    onConfirm={confirmDeleteAlias}
                    onCancel={() => setAliasToDelete(null)}
                />
            )}
        </div>
    );
}
